import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { format, isValid } from "date-fns";
import JsBarcode from "jsbarcode";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../Context/UserContextAPI";
import LoaderLayout from "../../components/Layout/LoaderLayout";
import UserTableInReport from "../../components/Users/UserTableInReports";
import request from "../../utils/request";
import getBarcodeContent from "../../utils/getBarcodeContent";
import { Button } from "react-bootstrap";
import { BsPrinter } from "react-icons/bs";

const CollectionLabel = () => {
  const { accessPerm, settingsData } = useContext(UserContext);
  const [isAutoPrint, setIsAutoPrint] = useState(true);
  const [specimenData, setSpecimenData] = useState([]);

  const { uId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["patientData"],
    queryFn: async () => {
      const res = await request.getAllById(`user-details/test`, uId);
      return res.status === "OK" ? res.data : null;
    },
    enabled: !!uId,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
  const patientData = data?.userDetail;

  const getInvIdsFromPatientData = useMemo(() => {
    let invArray = [];

    if (patientData?.user?.reInvstIds) {
      invArray = patientData.user.reInvstIds.split(",").map(Number);
    } else if (patientData?.user?.role_id === 4) {
      invArray = Array.isArray(patientData?.package?.investigation_id)
        ? patientData.package.investigation_id
        : patientData.package.investigation_id?.split(",").map(Number); // If it's a string, split and convert to numbers
    } else if (patientData?.user?.role_id === 5) {
      invArray = Array.isArray(patientData?.investigation_id)
        ? patientData.investigation_id
        : patientData.investigation_id?.split(",").map(Number); // If it's a string, split and convert to numbers
    }
    return invArray;
  }, [patientData]);

  const { data: specimenDataFromDB } = useQuery({
    queryKey: ["specimenData"],
    queryFn: async () => {
      const invIds = getInvIdsFromPatientData;
      const dataToSend = {
        invIds,
      };
      // its not create, but post request
      const res = await request.create(`collection/${uId}`, dataToSend);
      return Array.isArray(res) ? res : [];
    },
    enabled: !!getInvIdsFromPatientData?.length,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // set the specimen data to collectedSpecimens
  useEffect(() => {
    if (specimenDataFromDB?.length > 0) {
      setSpecimenData(
        specimenDataFromDB?.map((item) => ({
          ...item,
          isCreatedInDB: item?.createdAt !== "---",
        })),
      );
    }
  }, [specimenDataFromDB]);

  const patientId = getBarcodeContent(
    settingsData?.barcode_content,
    patientData?.user,
  );

  const Gender = parseInt(patientData?.gender?.id) === 1 ? "Male" : "Female";
  const birthYear = patientData?.user?.date_of_birth
    ? new Date(patientData?.user?.date_of_birth).getFullYear()
    : null;

  const currentYear = new Date().getFullYear();
  const age = birthYear ? `${currentYear - birthYear}Y` : "";

  const saveData = async (update) => {
    try {
      // Make the API call to create or update data
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/collection/create-update/${uId}`,
        update,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${localStorage?.getItem("jwtToken")}`,
          },
        },
      );
      queryClient.invalidateQueries({ queryKey: ["specimenData"] });
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  const handleSpecimenChange = (event, id) => {
    // const value = JSON.parse(event.target.value);
    // console.log("value", value);
    const isChecked = event.target.checked;

    const updatedSpecimenData = specimenData.map((specimen) => {
      if (specimen.id === id) {
        return {
          ...specimen,
          createdAt: isChecked ? new Date() : "---",
        };
      }
      return specimen;
    });

    setSpecimenData(updatedSpecimenData);

    // setSpecimenData((prevSelectedSpecimens) => [
    //   ...prevSelectedSpecimens,
    //   {
    //     ...specimen,
    //     createdAt: isChecked ? new Date().toLocaleString() : "---",
    //   },
    // ]);
  };

  const newSelectedSpecimens = () => {
    return specimenData.filter(
      (item) => item?.isCreatedInDB === false && item?.createdAt !== "---",
    );
  };

  const setAllSpecimentDone = () =>
    specimenData.map((item) => {
      if (item?.isCreatedInDB === false) {
        return {
          ...item,
          createdAt: new Date(),
        };
      }
      return item;
    });

  const isAllSpecimentDone = useMemo(() => {
    return specimenData.every((item) => item?.isCreatedInDB === true);
  }, [specimenData]);

  const handlePrint = async () => {
    const newSpecimens = newSelectedSpecimens();
    saveData(specimenData);
    printPopup(newSpecimens);
  };
  const handlePrintAll = async () => {
    const allDoneSpecimens = setAllSpecimentDone();
    saveData(allDoneSpecimens);
    printPopup(allDoneSpecimens);
  };

  const deleteSpecimenData = async () => {
    try {
      // console.log(dataToSend);
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/collection/delete/${uId}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${localStorage?.getItem("jwtToken")}`,
          },
        },
      );
      queryClient.invalidateQueries({ queryKey: ["specimenData"] });
    } catch (error) {
      console.log(error);
    }
  };

  // print popup
  const printPopup = (printingSpecimens) => {
    const isReInvestigation = patientData?.user?.reInvstIds;

    const printingData = isReInvestigation
      ? printingSpecimens.flatMap((specimen) =>
          specimen.investigations.map((inv) => ({
            ...inv,
            createdAt: specimen.createdAt,
          })),
        )
      : printingSpecimens;

    const newWindow = window.open(
      "about:blank",
      "newWin",
      `width=900,height=600,left=${window.screen.availWidth / 2 - 450},top=${
        window.screen.availHeight / 2 - 300
      }`,
    );

    newWindow.document.open();
    newWindow.document.write(
      `<html><head><title>Print</title></head><body>${printingData
        .map((specimen) => {
          const patientIdBarcode = document.createElement("canvas");
          JsBarcode(patientIdBarcode, String(patientId), {
            height: 30,
            margin: 0,
            displayValue: false,
          });

          const patientIdDataUrl = patientIdBarcode.toDataURL();
          const regNoInPdf =
            settingsData?.barcode_content === 3
              ? `<p style="margin: 0; font-size: 14px; font-weight: 900;">
                ${patientData?.user?.reg_prefix ?? ""}${patientData?.user?.registration_no}
              </p>`
              : "";

          return `<div class="container" style="text-align: left; font-size: 10px;">
          <div class="specimen" style="display: inline-block; text-align: center;">
            <p style="margin: 0; font-size: 12px; font-weight: 900;">${format(
              new Date(specimen?.createdAt),
              "dd-MM-yyyy p",
            )}</p>


            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
            ">
           ${regNoInPdf}
              <p style="margin: 0; font-size: 14px; font-weight: 900;">
                ${patientId}
              </p>
            </div>


            <p class="gender-dob" style="margin:0; font-weight: 900;">
              <span>Gender: ${Gender}</span>
                ${age !== "" ? "|" : ""}
                ${age !== "" ? `Age: ${age}` : ""}
            </p>

            <img src="${patientIdDataUrl}" class="barcode-img" style="display: block; margin: 0 auto; padding: 0; max-width: 100%; height: auto; max-height: 100px;" />
            <h3 style="margin: 4px 0;">${specimen?.name}</h3>
          </div>
        </div>`;
        })
        .join("")}</body></html>`,
    );
    newWindow.document.close();
    setTimeout(() => {
      const mediaQueryList = newWindow.matchMedia("print");

      mediaQueryList.addEventListener("change", (mqlEvent) => {
        if (!mqlEvent.matches) {
          newWindow.close();
        }
      });
      newWindow.print();
    }, 1000);
  };
  // auto print which speciment is not selectedu
  useEffect(() => {
    if (
      specimenData.length > 0 &&
      isAutoPrint &&
      settingsData?.collection_barcode_printing === 1
    ) {
      if (!isAllSpecimentDone) {
        const allDoneSpecimens = setAllSpecimentDone();
        const notSavedSpecimens = allDoneSpecimens.filter(
          (item) => item?.isCreatedInDB === false,
        );
        saveData(allDoneSpecimens);
        // print the new specimens
        printPopup(notSavedSpecimens);
        setIsAutoPrint(false);
      } else {
        setIsAutoPrint(false);
      }
    }
  }, [
    specimenData,
    isAutoPrint,
    isAllSpecimentDone,
    settingsData?.collection_barcode_printing,
  ]);

  return (
    <LoaderLayout isPending={isLoading}>
      <div className="card">
        <div className="card-body">
          <div className="border p-3 rounded">
            <div className="card-box">
              <h6 className="mb-0 text-uppercase">Collection Label </h6>
            </div>
            <UserTableInReport
              user={{
                ...patientData?.user,
                userDetail: patientData,
              }}
            />

            <div className="mt-3">
              <table className="table table-striped">
                <thead className="bg-primary text-white">
                  <tr>
                    <th scope="col">Specimen</th>
                    <th scope="col">Collection Date and Time</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {specimenData?.map((specimen, i) => (
                    <>
                      <tr key={i}>
                        {accessPerm(35, 15) && (
                          <>
                            <th scope="row">
                              <div className="form-check d-flex align-items-center">
                                <input
                                  className="form-check-input mt-0 me-2"
                                  type="checkbox"
                                  id={specimen?.name}
                                  name={specimen?.name}
                                  checked={specimen?.createdAt !== "---"}
                                  onChange={(e) =>
                                    handleSpecimenChange(e, specimen?.id)
                                  }
                                  disabled={specimen?.isCreatedInDB}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={specimen?.name}
                                >
                                  {specimen?.name}
                                </label>
                              </div>
                            </th>
                            <td>
                              {isValid(new Date(specimen?.createdAt))
                                ? format(
                                    new Date(specimen?.createdAt),
                                    "dd-MM-yyyy p",
                                  )
                                : "---"}
                            </td>
                            <td>
                              {specimen?.isCreatedInDB && (
                                <Button
                                  size="sm"
                                  onClick={() => printPopup([specimen])}
                                >
                                  <BsPrinter className="me-2" />
                                  Reprint
                                </Button>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    </>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="col-sm-12">
              <div className="btn-group btn-group-justified">
                {accessPerm(35, 23) && (
                  <button
                    onClick={handlePrint}
                    className="btn btn-primary px-5 m-2 rounded"
                    disabled={newSelectedSpecimens()?.length === 0}
                  >
                    Print
                  </button>
                )}
                {accessPerm(35, 24) && (
                  <button
                    onClick={handlePrintAll}
                    className="btn btn-primary px-5 m-2 rounded"
                    disabled={isAllSpecimentDone}
                  >
                    Print All
                  </button>
                )}

                <div className="">
                  {accessPerm(35, 8) && (
                    <button
                      className="btn btn-danger px-5 text-white m-2 rounded"
                      data-bs-toggle="modal"
                      data-bs-target={`#resetCollection`}
                    >
                      Reset
                    </button>
                  )}
                  <div
                    className="modal fade"
                    id={`resetCollection`}
                    tabIndex={-1}
                    style={{ display: "none" }}
                    aria-hidden="true"
                  >
                    <div className="modal-dialog modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">
                            Reset Collection Label
                          </h5>
                          <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                          />
                        </div>
                        <div className="modal-body pb-0">
                          <div className="mb-3 row ">
                            <div className="col-sm-10">
                              <p>
                                Are you sure you want to reset this collection
                                label?
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={deleteSpecimenData}
                            data-bs-dismiss="modal"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            data-bs-toggle="modal"
                            data-bs-target={`#resetCollection`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/dashboard/specimen-collection");
                  }}
                  className="btn btn-outline-secondary px-5 m-2 rounded"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoaderLayout>
  );
};

export default CollectionLabel;
